import React from 'react';
import VideoEmbed from '../components/VideoEmbed';
import videoManifest from '../data/videos.json';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TableOfContents from '../components/TableOfContents';
import securityContent from './docs/securityContent';
import localModelsContent from './docs/localModelsContent';
import gettingStartedContent from './docs/gettingStartedContent';
import promptEngineeringContent from './docs/promptEngineeringContent';
import rulesEngineContent from './docs/rulesEngineContent';
import spacesContent from './docs/spacesContent';
import byokContent from './docs/byokContent';
import finderTagsContent from './docs/finderTagsContent';
import autoTagSpacesContent from './docs/autoTagSpacesContent';
import watchFoldersAutomationsContent from './docs/watchFoldersAutomationsContent';
import importingRulepacksContent from './docs/importingRulepacksContent';
import fileSafetyContent from './docs/fileSafetyContent';
import languageSettingsContent from './docs/languageSettingsContent';
import connectedAiContent from './docs/connectedAiContent';
import clioIntegrationContent from './docs/clioIntegrationContent';

const DocTopicPage = () => {
  const { docSlug } = useParams();

  // Get content based on the slug
  let pageContent = null;
  if (docSlug === 'local-models') {
    pageContent = localModelsContent;
  } else if (docSlug === 'getting-started') {
    pageContent = gettingStartedContent;
  } else if (docSlug === 'prompt-engineering') {
    pageContent = promptEngineeringContent;
  } else if (docSlug === 'security') {
    pageContent = securityContent;
  } else if (docSlug === 'rules-engine') {
    pageContent = rulesEngineContent;
  } else if (docSlug === 'spaces') {
    pageContent = spacesContent;
  } else if (docSlug === 'byok') {
    pageContent = byokContent;
  } else if (docSlug === 'finder-tags') {
    pageContent = finderTagsContent;
  } else if (docSlug === 'auto-tag-spaces') {
    pageContent = autoTagSpacesContent;
  } else if (docSlug === 'watch-folders-automations') {
    pageContent = watchFoldersAutomationsContent;
  } else if (docSlug === 'importing-rulepacks') {
    pageContent = importingRulepacksContent;
  } else if (docSlug === 'file-safety') {
    pageContent = fileSafetyContent;
  } else if (docSlug === 'language-settings') {
    pageContent = languageSettingsContent;
  } else if (docSlug === 'connected-ai') {
    pageContent = connectedAiContent;
  } else if (docSlug === 'clio-integration') {
    pageContent = clioIntegrationContent;
  }

  // If no matching content is found, show under construction message
  if (!pageContent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>{docSlug} | Sortio Documentation</title>
          <meta name="description" content={`Documentation for ${docSlug} in Sortio - AI-powered file organization tool`} />
        </Helmet>
        
        <Header />
        
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold mb-8 text-center">{docSlug.replace(/-/g, ' ')}</h1>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-4">Under Construction</h2>
              <p className="text-gray-600">
                This documentation topic is currently being developed. Please check back soon.
              </p>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  // Generate table of contents from sections
  const tableOfContents = pageContent.sections.map(section => ({
    id: section.id,
    title: section.title
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f5f7] to-white">
      <Helmet>
        <title>{pageContent.title} | Sortio Documentation</title>
        <meta name="description" content={pageContent.description} />
      </Helmet>
      
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        <div className="flex flex-col md:flex-row gap-10">
          {/* Sidebar */}
          <div className="md:w-72 shrink-0">
            <div className="sticky top-24">
              <div className="mb-8">
                <Link to="/docs" className="text-[#06c] hover:text-[#0077e6] flex items-center transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">Back to Documentation</span>
                </Link>
              </div>
              
              <div className="backdrop-blur-xl bg-white/70 rounded-2xl shadow-sm border border-[#e0e0e5] p-5">
                <h3 className="font-semibold text-[#1d1d1f] text-lg mb-4">On This Page</h3>
                <TableOfContents sections={tableOfContents} />
              </div>
            </div>
          </div>
          
          {/* Main content */}
          <div className="flex-1">
            <div className="backdrop-blur-xl bg-white/70 rounded-2xl shadow-sm border border-[#e0e0e5] p-8 md:p-12">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold text-[#1d1d1f] mb-4">{pageContent.title}</h1>
                <p className="text-[#86868b] text-lg mb-12">{pageContent.description}</p>

                {videoManifest[`/docs/${docSlug}`] && (
                  <VideoEmbed video={videoManifest[`/docs/${docSlug}`]} />
                )}
                
                {/* Documentation sections */}
                {pageContent.sections.map((section) => (
                  <section key={section.id} id={section.id} className="mb-16">
                    <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-6">{section.title}</h2>
                    <div 
                      className="prose prose-apple max-w-none prose-headings:text-[#1d1d1f] prose-p:text-[#424245] prose-a:text-[#06c] prose-a:no-underline hover:prose-a:text-[#0077e6]" 
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocTopicPage;